import React, { useState, useEffect } from 'react';
import {
  Drawer,
  IconButton,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Close, Settings, Add, Delete, Edit } from '@mui/icons-material';
import { toast } from 'react-toastify';

const ParameterDrawer = ({ open, onClose, connectorId, connectorName }) => {
  const [parameters, setParameters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentParameter, setCurrentParameter] = useState(null);
  const [paramValue, setParamValue] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isRequired, setIsRequired] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false); // New state for delete confirmation dialog
  const [parameterToDelete, setParameterToDelete] = useState(null); // New state to track parameter to delete

  const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/master-connector`;

  // Fetch parameters when drawer opens or connector changes
  useEffect(() => {
    if (open && connectorId) {
      fetchParameters();
    }
  }, [open, connectorId]);

  const fetchParameters = async () => {
    if (!connectorId) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/${connectorId}/parameters`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Map encryptionRequired to isRequired for frontend use
      const mappedData = data.map(param => ({
        ...param,
        isRequired: param.encryptionRequired
      }));
      setParameters(mappedData);
    } catch (error) {
      console.error('Error fetching parameters:', error);
      toast.error(`Failed to load parameters: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddParameter = async () => {
    if (!paramValue.trim()) {
      toast.warning('Parameter cannot be empty');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/parameters`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          mcpParam: paramValue,
          masterConnectorId: connectorId,
          encryptionRequired: isRequired // Changed to match backend
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Map encryptionRequired to isRequired
      setParameters([...parameters, { ...data, isRequired: data.encryptionRequired }]);
      setParamValue('');
      setIsRequired(false);
      setDialogOpen(false);
      toast.success('Parameter added successfully');
    } catch (error) {
      console.error('Error adding parameter:', error);
      toast.error(`Failed to add parameter: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditParameter = (parameter) => {
    setCurrentParameter(parameter);
    setParamValue(parameter.mcpParam);
    setIsRequired(parameter.isRequired); // Use mapped isRequired
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleUpdateParameter = async () => {
    if (!paramValue.trim() || !currentParameter) {
      toast.warning('Parameter cannot be empty');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/parameters/${currentParameter.mcpId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          mcpParam: paramValue,
          encryptionRequired: isRequired // Changed to match backend
        })
      });

      if (!response.ok) {
        console.error('Server response:', await response.text());
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedParameter = await response.json();
      // Map encryptionRequired to isRequired
      const mappedParameter = { ...updatedParameter, isRequired: updatedParameter.encryptionRequired };
      setParameters(parameters.map(param =>
        param.mcpId === currentParameter.mcpId ? mappedParameter : param
      ));

      setEditMode(false);
      setCurrentParameter(null);
      setParamValue('');
      setIsRequired(false);
      setDialogOpen(false);
      toast.success('Parameter updated successfully');
    } catch (error) {
      console.error('Error updating parameter:', error);
      toast.error(`Failed to update parameter: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

const handleDeleteParameter = (parameterId) => {
    // Open the delete confirmation dialog instead of using window.confirm
    const parameter = parameters.find(param => param.mcpId === parameterId);
    setParameterToDelete(parameter);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!parameterToDelete) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/parameters/${parameterToDelete.mcpId}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('Server response:', await response.text());
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setParameters(parameters.filter(param => param.mcpId !== parameterToDelete.mcpId));
      toast.success('Parameter deleted successfully');

      if (currentParameter && currentParameter.mcpId === parameterToDelete.mcpId) {
        setEditMode(false);
        setCurrentParameter(null);
        setParamValue('');
        setIsRequired(false);
        setDialogOpen(false);
      }
    } catch (error) {
      console.error('Error deleting parameter:', error);
      toast.error(`Failed to delete parameter: ${error.message}`);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setParameterToDelete(null);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setCurrentParameter(null);
    setParamValue('');
    setIsRequired(false);
    setDialogOpen(false);
  };

  const handleOpenDialog = () => {
    setParamValue('');
    setIsRequired(false);
    setEditMode(false);
    setCurrentParameter(null);
    setDialogOpen(true);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{ '& .MuiDrawer-paper': { width: 400 } }}
      >
        <Box sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center">
              <Settings sx={{ mr: 1 }} />
              <Typography variant="h6">Parameters</Typography>
            </Box>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>

          <Typography variant="subtitle1" gutterBottom>
            Connector: {connectorName || 'Unknown'}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Box mb={3}>
            <Box display="flex" alignItems="center">
              {/* <Typography variant="subtitle2" gutterBottom>
                Add New Parameter
              </Typography>
              <IconButton
                size="small"
                onClick={handleOpenDialog}
                sx={{ ml: 1 }}
              >
                <Add />
              </IconButton> */}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1"></Typography>
                        <Button
                          startIcon={<Add />}
                          onClick={handleOpenDialog}
                          variant="outlined"
                          size="small"
                        >
                          Add New Parameter
                        </Button>
                      </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Parameter List
          </Typography>

          {loading && !parameters.length ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : parameters.length === 0 ? (
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography color="textSecondary">
                No parameters found for this connector
              </Typography>
            </Paper>
          ) : (
            <List sx={{ maxHeight: 500, overflow: 'auto' }}>
              {parameters.map((param) => (
                <React.Fragment key={param.mcpId}>
                  <ListItem
                    secondaryAction={
                      <Box>
                        <IconButton
                          edge="end"
                          size="small"
                          color="primary"
                          onClick={() => handleEditParameter(param)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          edge="end"
                          size="small"
                          color="error"
                          onClick={() => handleDeleteParameter(param.mcpId)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={param.mcpParam}
                      secondary={param.isRequired ? 'Encryption Required' : 'Encryption Not Required'}
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Drawer>

      <Dialog open={dialogOpen} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Parameter' : 'Add Parameter'}
          <IconButton
            aria-label="close"
            onClick={handleCancel}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box>
            <TextField
              fullWidth
              size="small"
              label="Parameter"
              value={paramValue}
              onChange={(e) => setParamValue(e.target.value)}
              placeholder="Enter parameter name"
              disabled={loading}
              margin="normal"
            />
            <FormControlLabel
              label="Encryption Required?"
              control={
                <Checkbox
                  checked={isRequired}
                  onChange={(e) => setIsRequired(e.target.checked)}
                  disabled={loading}
                />
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            variant="contained"
            color={editMode ? "primary" : "primary"}
            onClick={editMode ? handleUpdateParameter : handleAddParameter}
            disabled={!paramValue.trim() || loading}
          >
            {loading ? <CircularProgress size={24} /> : editMode ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the parameter "{parameterToDelete?.mcpParam}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ParameterDrawer;